import Link from 'next/link'
import CommunityCta from '../components/CommunityCta'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SEO from '../components/SEO'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import FOR_COMMUNITY_COPY from '../locales/forCommunity'


export default function ForCommunityPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <ForCommunity />
    </LanguageProvider>
  )
}


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


function ForCommunity() {
  const { language } = useLanguage()
  const t = FOR_COMMUNITY_COPY[language] || FOR_COMMUNITY_COPY.en
  // The apply button below is now rendered via <CommunityCta>, which
  // fetches /v1/discounts/status/ and switches label + destination
  // based on the user's actual state in the program (apply / under
  // review / accept / manage / re-attest). See CommunityCta component
  // + backend views.CommunityStatusView. Anonymous auth handling is
  // done inside the component (login-first CTA).

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <SEO
        title={t.seo.title}
        description={t.seo.description}
        path="/for-community"
        lang={language}
      />
      <LanguageSwitcher />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.header.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t.header.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-3xl mb-3">🇭🇹</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t.grant.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">
              {t.grant.body}
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              {t.grant.bullets.map((b, i) => (<li key={i}>• {b}</li>))}
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-3xl mb-3">🌎</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t.mission.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">
              {t.mission.body}
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              {t.mission.bullets.map((b, i) => (<li key={i}>• {b}</li>))}
            </ul>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.qualifies.h2}
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-300">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t.qualifies.nonprofits.title}
              </h3>
              <p>
                {t.qualifies.nonprofits.body}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t.qualifies.community.title}
              </h3>
              <p>
                {t.qualifies.community.body}
              </p>
              <ul className="mt-2 ml-5 list-disc space-y-1">
                {t.qualifies.community.bullets.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.how_it_works.h2}
          </h2>
          <ol className="space-y-3 text-gray-600 dark:text-gray-300 list-decimal list-inside">
            {t.how_it_works.steps.map((s, i) => (<li key={i}>{s}</li>))}
          </ol>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border-l-4 border-amber-500">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.attribution.h2}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-3">
            {t.attribution.body}
          </p>
          <Link
            href="/attribution"
            className="text-blue-600 hover:underline text-sm"
          >
            {t.attribution.link}
          </Link>
        </div>

        <div className="text-center">
          <CommunityCta variant="default" />

          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t.questions_prefix}
            <a href="mailto:support@creolecentric.com" className="underline">
              support@creolecentric.com
            </a>
          </p>
        </div>

      </div>
    </div>
  )
}
